import { Injectable, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, Subject, fromEvent } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { environment } from '../../config/editor-environment';
import { AuthService } from '../auth/auth.service';
import { WebSocketConnectionState, WebSocketMessage } from './websocket.types';

export { WebSocketConnectionState, WebSocketMessage } from './websocket.types';

@Injectable({
  providedIn: 'root'
})
export class WebSocketService implements OnDestroy {
  private authService = inject(AuthService);

  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private heartbeatTimer: any;
  private connectionTimer: any;
  private messageQueue: WebSocketMessage[] = [];

  // Connection state management
  private connectionState$ = new BehaviorSubject<WebSocketConnectionState>(WebSocketConnectionState.DISCONNECTED);
  private messages$ = new Subject<WebSocketMessage>();
  private errors$ = new Subject<Error>();

  // Configuration
  private readonly wsUrl = environment.SOCKET_ADDRESS;
  private readonly maxReconnectAttempts = 10;
  private readonly heartbeatInterval = 30000; // 30 seconds
  private readonly connectionTimeout = 10000; // 10 seconds

  constructor() {
    // WebSocketService is providedIn:'root' — instantiated lazily when first
    // injected, often before authService.isAuthenticated() flips to true
    // (e.g. before the refresh-token flow stores the access token in memory).
    // Try once now, then poll briefly so the connection eventually opens
    // once the token is available — without requiring every consumer to
    // call connect() defensively.
    if (this.authService.isAuthenticated()) {
      this.connect();
    } else {
      this.scheduleAuthAwareConnect();
    }
    this.setupNetworkListener();
  }

  private scheduleAuthAwareConnect(): void {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      const connected = this.connectionState$.value === WebSocketConnectionState.CONNECTED;
      if (connected || attempts > 30) {
        clearInterval(interval);
        return;
      }
      if (this.authService.isAuthenticated()) {
        clearInterval(interval);
        this.connect();
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Get the current connection state as an observable
   */
  get connectionState(): Observable<WebSocketConnectionState> {
    return this.connectionState$.asObservable();
  }

  /**
   * Get incoming messages as an observable
   */
  get messages(): Observable<WebSocketMessage> {
    return this.messages$.asObservable();
  }

  /**
   * Get connection errors as an observable
   */
  get errors(): Observable<Error> {
    return this.errors$.asObservable();
  }

  /**
   * Check if currently connected
   */
  get isConnected(): boolean {
    return this.connectionState$.value === WebSocketConnectionState.CONNECTED;
  }

  /**
   * Establish WebSocket connection
   */
  connect(): void {
    if (this.socket?.readyState === WebSocket.CONNECTING || this.socket?.readyState === WebSocket.OPEN) {
      return; // Already connecting or connected
    }

    if (!this.authService.isAuthenticated()) {
      console.warn('WebSocket: Cannot connect - no valid token available');
      return;
    }

    this.updateConnectionState(WebSocketConnectionState.CONNECTING);

    try {
      const token = this.authService.getAccessToken();
      if (!token) {
        throw new Error('No access token available');
      }
      const socketUrl = `${this.wsUrl}?token=${encodeURIComponent(token)}&source=editor`;

      this.socket = new WebSocket(socketUrl);
      this.setupSocketEventHandlers();
      this.startConnectionTimeout();
    } catch (error) {
      console.error('WebSocket: Failed to create connection', error);
      this.handleConnectionError(error);
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    this.clearTimers();
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = null;
    }

    this.updateConnectionState(WebSocketConnectionState.DISCONNECTED);
  }

  /**
   * Send message through WebSocket
   */
  sendMessage<T>(event: string, data: T): void {
    const message: WebSocketMessage<T> = {
      event,
      data,
      timestamp: Date.now(),
      id: this.generateMessageId()
    };

    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        console.error('WebSocket: Failed to send message', error);
        this.queueMessage(message);
      }
    } else {
      // Queue message for when connection is restored
      this.queueMessage(message);

      // Attempt to reconnect if not already trying
      if (this.connectionState$.value === WebSocketConnectionState.DISCONNECTED) {
        this.reconnect();
      }
    }
  }

  /**
   * Send raw payload (for assistant chat compatibility)
   */
  sendRawMessage(payload: any): void {
    if (this.isConnected && this.socket) {
      try {
        this.socket.send(JSON.stringify(payload));
      } catch (error) {
        console.error('WebSocket: Failed to send raw message', error);
        // For raw messages, we don't queue them as they're likely time-sensitive
      }
    } else {
      console.warn('WebSocket: Cannot send raw message - not connected');

      // Attempt to reconnect if not already trying
      if (this.connectionState$.value === WebSocketConnectionState.DISCONNECTED) {
        this.reconnect();
      }
    }
  }

  /**
   * Subscribe to specific message types
   */
  onMessage<T>(): Observable<WebSocketMessage<T>> {
    return this.messages$.asObservable();
  }

  /**
   * Force reconnection
   */
  reconnect(): void {
    this.disconnect();
    setTimeout(() => this.connect(), 100);
  }

  private setupSocketEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = (event) => {
      this.updateConnectionState(WebSocketConnectionState.CONNECTED);
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.processMessageQueue();
      this.clearConnectionTimeout();
    };

    this.socket.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.messages$.next(message);
      } catch (error) {
        console.error('WebSocket: Failed to parse message', error);
      }
    };

    this.socket.onclose = (event) => {
      this.clearTimers();

      if (event.code === 1000) {
        // Normal closure
        this.updateConnectionState(WebSocketConnectionState.CLOSED);
      } else {
        // Unexpected closure - attempt reconnection
        this.handleConnectionError(new Error(`Connection closed unexpectedly: ${event.reason}`));
      }
    };

    this.socket.onerror = (event) => {
      console.error('WebSocket: Connection error', event);
      this.handleConnectionError(new Error('WebSocket connection error'));
    };
  }

  private setupNetworkListener(): void {
    // Listen for online/offline events
    fromEvent(window, 'online')
      .pipe(
        takeUntilDestroyed(),
        takeWhile(() => this.authService.isAuthenticated())
      )
      .subscribe(() => {
        if (this.connectionState$.value !== WebSocketConnectionState.CONNECTED) {
          this.reconnect();
        }
      });

    fromEvent(window, 'offline')
      .pipe(
        takeUntilDestroyed(),
        takeWhile(() => this.authService.isAuthenticated())
      )
      .subscribe(() => {
        this.updateConnectionState(WebSocketConnectionState.ERROR);
      });
  }

  private handleConnectionError(error: any): void {
    console.error('WebSocket: Connection error', error);
    this.errors$.next(error);
    this.clearTimers();

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.updateConnectionState(WebSocketConnectionState.RECONNECTING);
      this.scheduleReconnection();
    } else {
      console.error('WebSocket: Max reconnection attempts reached');
      this.updateConnectionState(WebSocketConnectionState.ERROR);
    }
  }

  private scheduleReconnection(): void {
    this.reconnectAttempts++;
    const delay = this.calculateReconnectDelay();

    setTimeout(() => {
      if (this.authService.isAuthenticated()) {
        this.connect();
      }
    }, delay);
  }

  private calculateReconnectDelay(): number {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const baseDelay = 1000; // 1 second
    const exponentialDelay = baseDelay * Math.pow(2, this.reconnectAttempts - 1);
    return Math.min(exponentialDelay, 30000); // Cap at 30 seconds
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.sendMessage('ping', { timestamp: Date.now() });
      }
    }, this.heartbeatInterval);
  }

  private startConnectionTimeout(): void {
    this.connectionTimer = setTimeout(() => {
      if (this.socket?.readyState === WebSocket.CONNECTING) {
        console.error('WebSocket: Connection timeout');
        this.socket.close();
        this.handleConnectionError(new Error('Connection timeout'));
      }
    }, this.connectionTimeout);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimer) {
      clearTimeout(this.connectionTimer);
      this.connectionTimer = null;
    }
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.clearConnectionTimeout();
  }

  private updateConnectionState(state: WebSocketConnectionState): void {
    if (this.connectionState$.value !== state) {
      this.connectionState$.next(state);
    }
  }

  private queueMessage(message: WebSocketMessage): void {
    this.messageQueue.push(message);

    // Limit queue size to prevent memory issues
    if (this.messageQueue.length > 100) {
      this.messageQueue.shift(); // Remove oldest message
    }
  }

  private processMessageQueue(): void {
    if (!this.isConnected || this.messageQueue.length === 0) {
      return;
    }

    while (this.messageQueue.length > 0 && this.isConnected) {
      const message = this.messageQueue.shift();
      if (message && this.socket) {
        try {
          this.socket.send(JSON.stringify(message));
        } catch (error) {
          console.error('WebSocket: Failed to send queued message', error);
          // Re-queue the message
          this.messageQueue.unshift(message);
          break;
        }
      }
    }
  }

  private generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}
